import { useState, useEffect, useRef, useCallback } from 'react';
import { formattedPrice, percentageOff } from '../Cart/util';

const VariantBox = ({ 
    variants, 
    pricingType, 
    handleAddItem, 
    variables,
    setSelectedVariant,
    handleVariantBoxSelection,
    boxType,
    theme
}) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [gridColumns, setGridColumns] = useState(2);
    const [useSlider, setUseSlider] = useState(false);
    const containerRef = useRef(null);
    const [slideWidth, setSlideWidth] = useState(0);
    const [gapWidth, setGapWidth] = useState(10);
    const [touchStart, setTouchStart] = useState(null);
    const [touchEnd, setTouchEnd] = useState(null);
    
    const calculateGridColumns = useCallback(() => {
        let columns;
        if (boxType === 'list' && theme !== 'boxes-slider') {
            if (window.innerWidth > 1399) {
                columns = 6;
            } else if (window.innerWidth > 992) {
                columns = 4;
            } else if (window.innerWidth > 299) {
                columns = 4;
            } else {
                columns = 3;
            }
        } else if (theme === 'boxes-slider' && boxType !== 'list') {
            if (window.innerWidth > 1399) {
                columns = 2;
            } else if (window.innerWidth > 991) {
                columns = 3;
            } else {
                columns = 2;
            }
        } else if (theme === 'boxes-slider' && boxType == 'list' && window.innerWidth > 375) {
            columns = 4;
        } else {
            columns = 3;
        }
        return columns;
    }, [boxType, theme]);

    const calculateSlideWidth = useCallback(() => {
        if (containerRef.current && useSlider) {
            const containerWidth = containerRef.current.offsetWidth;
            const arrowWidth = 19;
            const availableWidth = containerWidth - (2 * arrowWidth);
            return (availableWidth - ((gridColumns - 1) * gapWidth)) / gridColumns;
        }
        return 0;
    }, [useSlider, gridColumns, gapWidth]);

    const handleResize = useCallback(() => {
        const newGridColumns = calculateGridColumns();
        setGridColumns(newGridColumns);
        setUseSlider(theme === 'boxes-slider');
        setSlideWidth(calculateSlideWidth());
        setCurrentIndex(0); // Reset to first slide on resize
    }, [calculateGridColumns, calculateSlideWidth, theme, variants.length]);

    useEffect(() => {
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [handleResize]);

    useEffect(() => {
        setSlideWidth(calculateSlideWidth());
    }, [calculateSlideWidth, gridColumns, useSlider]);

    const nextSlide = () => {
        setCurrentIndex((prevIndex) => 
            Math.min(prevIndex + 1, variants.length - gridColumns)
        );
    };

    const prevSlide = () => {
        setCurrentIndex((prevIndex) => Math.max(prevIndex - 1, 0));
    };
    
    const handleTouchStart = (e) => {
        setTouchEnd(null);
        setTouchStart(e.targetTouches[0].clientX);
    }

    const handleTouchMove = (e) => {
        setTouchEnd(e.targetTouches[0].clientX);
    }

    const handleTouchEnd = () => {
        if (!touchStart || !touchEnd) return;
        const distance = touchStart - touchEnd;
        const isLeftSwipe = distance > 50;
        const isRightSwipe = distance < -50;

        if (isLeftSwipe && currentIndex < variants.length - gridColumns) {
            nextSlide();
        } else if (isRightSwipe && currentIndex > 0) {
            prevSlide();
        }
    }

    const renderVariant = (variant, index) => {
        const specialPrice = variant[pricingType.special];
        const standardPrice = variant[pricingType.standard];
        const hasSpecialPrice = specialPrice && specialPrice < standardPrice;

        const isVisible = useSlider
            ? index >= currentIndex && index < currentIndex + gridColumns
            : true;

        return (
            <div
                key={variant.id}
                className={`variant-slide ${isVisible ? 'visible' : ''}`}
                style={{
                    width: useSlider ? `${slideWidth}px` : 'auto',
                }}
                onClick={() => handleVariantBoxSelection(variant)}
            >
                {hasSpecialPrice && (
                    <div className='variant-price-discount'>{`${percentageOff(
                        variant[pricingType.standard],
                        variant[pricingType.special]
                    )}% off`}</div>
                )}
                <div className='variant-slide-box'>
                    <div className='variant-option'>{variant.option}</div>
                    <div className='variant-price'>
                        {hasSpecialPrice ? (
                            <div className='variant-special-price'>
                                <div className='variant-special-price-price'>
                                    <s className='headless-cart-item-price__strikethrough'>
                                        {formattedPrice(standardPrice)}
                                    </s>
                                    {' '}
                                    <span className='headless-pricing-type-special'>
                                        {formattedPrice(specialPrice)}
                                    </span>
                                </div>
                            </div>
                        ) : (
                            <span>{formattedPrice(standardPrice)}</span>
                        )}
                    </div>
                    <div className='variant-add'>
                        <svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 448 512'><path d='M256 80c0-17.7-14.3-32-32-32s-32 14.3-32 32l0 144L48 224c-17.7 0-32 14.3-32 32s14.3 32 32 32l144 0 0 144c0 17.7 14.3 32 32 32s32-14.3 32-32l0-144 144 0c17.7 0 32-14.3 32-32s-14.3-32-32-32l-144 0 0-144z' /></svg>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div
            ref={containerRef}
            className={`headless-variant-box-container ${useSlider ? 'use-slider' : ''} ${theme === 'boxes' ? 'boxes-no-slider' : ''}`}
            style={{ '--grid-columns': gridColumns, '--total-variants': variants.length }}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
        >
            {useSlider && currentIndex > 0 && (
                <button className='slider-arrow left' onClick={prevSlide} aria-label='Previous products'>
                    <svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 320 512'><path d='M9.4 233.4c-12.5 12.5-12.5 32.8 0 45.3l192 192c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L77.3 256 246.6 86.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0l-192 192z' /></svg>
                </button>
            )}
            <div className='variant-container' data-grid-columns={gridColumns}>
                <div className='variant-wrapper' style={{
                    transform: useSlider ? `translateX(calc(-${currentIndex * (slideWidth + gapWidth)}px))` : 'none',
                    transition: 'transform 0.3s ease-in-out',
                }}>
                    {variants.map(renderVariant)}
                </div>
            </div>
            {useSlider && currentIndex < variants.length - gridColumns && (
                <button className='slider-arrow right' onClick={nextSlide} aria-label='Next products'>
                    
                    <svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 320 512' width="20" height="20">
                        <path fill="currentColor" d='M310.6 233.4c12.5 12.5 12.5 32.8 0 45.3l-192 192c-12.5 12.5-32.8 12.5-45.3 0s-12.5-32.8 0-45.3L242.7 256 73.4 86.6c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0l192 192z' />
                    </svg>
              </button>
            )}
        </div>
    );
};

export default VariantBox;
